(() => {
  if (customElements.get('dd-review-summary')) return;

  const summaries = new Map();

  async function loadSummary(store, productId) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);

    async function readJson(url) {
      const response = await fetch(url, { signal: controller.signal, credentials: 'omit' });
      if (!response.ok) throw new Error('Stamped summary unavailable');
      return response.json();
    }

    try {
      // Use the same public configuration and display API as Stamped's installed widget.
      const configUrl = new URL('https://stamped.io/api/getappkey');
      configUrl.searchParams.set('shopShopifyDomain', store);
      const config = await readJson(configUrl);
      if (!config.apiKey?.startsWith('pubkey-') || !config.sId) throw new Error('Missing public Stamped configuration');

      const summaryUrl = new URL('https://stamped.io/api/widget/reviews');
      summaryUrl.search = new URLSearchParams({
        type: productId ? 'full-page' : 'site-badge',
        apiKey: config.apiKey,
        sId: config.sId,
        storeUrl: store,
        minRating: '1',
      }).toString();
      if (productId) summaryUrl.searchParams.set('productId', productId);
      const data = await readJson(summaryUrl);

      // Full-page fields follow productId; site-badge *All fields cover the store.
      const rating = productId ? data.rating : data.ratingAll;
      const count = productId ? data.total : data.totalAll;
      if (!Number.isFinite(rating) || rating <= 0 || rating > 5 || !Number.isSafeInteger(count) || count < 1) {
        throw new Error('Invalid Stamped aggregate');
      }
      return { rating, count };
    } finally {
      window.clearTimeout(timeout);
    }
  }

  customElements.define('dd-review-summary', class extends HTMLElement {
    connectedCallback() {
      const store = this.dataset.store;
      if (!store || this.loading) return;
      const rawProductId = this.dataset.productId;
      let productId = null;
      if (rawProductId !== undefined) {
        const numericProductId = Number(rawProductId);
        if (!/^\d+$/.test(rawProductId) || !Number.isSafeInteger(numericProductId) || numericProductId < 1) {
          this.hidden = true;
          return;
        }
        productId = String(numericProductId);
      }
      const cacheKey = JSON.stringify([store, productId]);
      this.loading = true;
      if (!summaries.has(cacheKey)) summaries.set(cacheKey, loadSummary(store, productId));

      summaries.get(cacheKey).then(({ rating, count }) => {
        if (!this.isConnected) {
          this.loading = false;
          return;
        }
        const locale = document.documentElement.lang || 'en';
        const ratingText = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: productId ? 2 : 1 }).format(rating);
        const countText = new Intl.NumberFormat(locale).format(count);
        const template = count === 1 ? this.dataset.summaryTemplateOne : this.dataset.summaryTemplate;
        this.querySelector('[data-dd-review-summary-text]').textContent = template.replace('[rating]', ratingText).replace('[count]', countText);
        this.querySelector('[data-dd-review-stars]').style.width = `${rating / 5 * 100}%`;
        this.hidden = false;
      }).catch(() => {
        // Hide unavailable totals without falling back to a different review scope.
        this.hidden = true;
      });
    }
  });
})();
